import {
  CityHallDefenseSlot,
  CityHallDefenseTier,
  CityHallIncomingRaid,
  CityHallMyRaid,
  CityHallPoint,
  CityHallRaidCombatCard,
  CityHallRaidCombatLog,
  CityHallRaidCombatRound,
  CityHallRaidDetail,
  CityHallRaidGeoBody,
  CityHallRaidOutcome,
  CityHallRaidPhase,
  CityHallRaidPreview,
  CityHallRaidRewards,
  CityHallRaidStatus,
  CityHallRaidTarget,
  CityHallSeasonEntry,
  CityHallStatus,
  CITY_HALL_RAID_ATTACKER_CAP_DEFAULT,
  CITY_HALL_RAID_REMOTE_FEE_DEFAULT,
  DEFAULT_CITY_HALL_RAID_REWARDS,
} from "../types/cityHall"
import { normalizeGameCard, normalizeGameCardsList } from "./gameCards"

type RawRecord = Record<string, unknown>

const DEFAULT_RADIUS_M = 50

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0
  if (typeof value === "string") {
    const key = value.trim().toLowerCase()
    if (key === "true" || key === "1" || key === "yes") return true
    if (key === "false" || key === "0" || key === "no") return false
  }
  return fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

export function normalizeCityHallPoint(raw: unknown): CityHallPoint | null {
  if (raw == null) return null
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.uuid ?? item.pointId ?? item.point_id
  const lat = pickNumber(item.latitude ?? item.lat)
  const lng = pickNumber(item.longitude ?? item.lng ?? item.lon)
  if (id == null || lat == null || lng == null) return null

  return {
    id: String(id),
    name: pickString(item.name ?? item.title, "Ратуша"),
    latitude: lat,
    longitude: lng,
    radiusMeters:
      pickNumber(
        item.radiusMeters ?? item.radius_meters ?? item.radiusM ?? item.radius,
        DEFAULT_RADIUS_M
      ) ?? DEFAULT_RADIUS_M,
    cityId: pickNumber(item.cityId ?? item.city_id) ?? undefined,
  }
}

function normalizeSeasonEntry(raw: unknown, index: number): CityHallSeasonEntry | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const userId = pickNumber(item.userId ?? item.user_id)
  if (userId == null) return null
  return {
    rank: pickNumber(item.rank, index + 1) ?? index + 1,
    userId,
    nickname: pickString(item.nickname ?? item.name, "—"),
    publicGameCode: pickString(
      item.publicGameCode ?? item.public_game_code,
      "—"
    ),
    localPts:
      pickNumber(
        item.localPts ?? item.local_pts ?? item.cityPts ?? item.city_pts ?? item.pts,
        0
      ) ?? 0,
    globalPts: pickNumber(item.globalPts ?? item.global_pts, 0) ?? 0,
  }
}

export function normalizeCityHallStatus(raw: unknown): CityHallStatus | null {
  const obj = unwrapPayload(raw)
  if (!obj || typeof obj !== "object") return null

  const sharesRaw = obj.payoutShares ?? obj.payout_shares
  const payoutShares = Array.isArray(sharesRaw)
    ? sharesRaw
        .map((value) => pickNumber(value, 0) ?? 0)
        .filter((value) => Number.isFinite(value))
    : [0.5, 0.3, 0.2]

  const leaderboardRaw = obj.seasonLeaderboard ?? obj.season_leaderboard
  const seasonLeaderboard = Array.isArray(leaderboardRaw)
    ? leaderboardRaw
        .map((entry, index) => normalizeSeasonEntry(entry, index))
        .filter((entry): entry is CityHallSeasonEntry => entry != null)
    : []

  const pointRaw = obj.cityHallPoint ?? obj.city_hall_point ?? obj.point

  return {
    cityId: pickNumber(obj.cityId ?? obj.city_id, 0) ?? 0,
    championFundCoins:
      pickNumber(obj.championFundCoins ?? obj.champion_fund_coins, 0) ?? 0,
    seasonMonthKey: pickString(
      obj.seasonMonthKey ?? obj.season_month_key,
      ""
    ),
    minPayoutCoins:
      pickNumber(obj.minPayoutCoins ?? obj.min_payout_coins, 100) ?? 100,
    payoutShares,
    seasonLeaderboard,
    myLocalPts: pickNumber(obj.myLocalPts ?? obj.my_local_pts, 0) ?? 0,
    myGlobalPts: pickNumber(obj.myGlobalPts ?? obj.my_global_pts, 0) ?? 0,
    cityHallPoint: normalizeCityHallPoint(pointRaw),
  }
}

/** Подпись «Сезон MM.YYYY» из seasonMonthKey «2026-07». */
export function formatSeasonLabel(seasonMonthKey?: string | null): string {
  const key = pickString(seasonMonthKey)
  const match = key.match(/^(\d{4})-(\d{2})$/)
  if (!match) return "Сезон"
  return `Сезон ${match[2]}.${match[1]}`
}

/** Europe/Minsk без DST с 2011 — постоянно UTC+3. */
const MINSK_OFFSET_MS = 3 * 60 * 60 * 1000

/** Мс до конца текущего месяца (Europe/Minsk, 1-е число 00:10 — выплата). */
export function getSeasonCountdownMs(now = Date.now()): number {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Minsk",
    year: "numeric",
    month: "numeric",
  })
  const parts = formatter.formatToParts(new Date(now))
  const get = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value)
  const year = get("year")
  const month = get("month")
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return 0
  }
  const nextMonth = month === 12 ? 1 : month + 1
  const nextYear = month === 12 ? year + 1 : year
  const endMs =
    Date.UTC(nextYear, nextMonth - 1, 1, 0, 10, 0, 0) - MINSK_OFFSET_MS
  const remaining = endMs - now
  return Number.isFinite(remaining) ? Math.max(0, remaining) : 0
}

export function formatSeasonCountdown(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "Скоро выплата"
  const totalMinutes = Math.floor(ms / 60_000)
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `${days} д ${hours} ч`
  if (hours > 0) return `${hours} ч ${minutes} мин`
  return `${minutes} мин`
}

function normalizeRaidPhase(raw: unknown): CityHallRaidPhase | null {
  const key = pickString(raw).toUpperCase()
  if (
    key === "VOTE" ||
    key === "COMMIT" ||
    key === "RESOLVED" ||
    key === "CANCELLED"
  ) {
    return key
  }
  return null
}

function normalizeDefenseTier(raw: unknown): CityHallDefenseTier | null {
  const key = pickString(raw).toUpperCase()
  if (key === "WEAK" || key === "MEDIUM" || key === "STRONG") return key
  return null
}

function normalizeRaidOutcome(raw: unknown): CityHallRaidOutcome | null {
  const key = pickString(raw).toUpperCase()
  if (!key) return null
  return key
}

function emptyDefense(): CityHallDefenseSlot {
  return { cardIds: [], cards: [], power: 0 }
}

function normalizeDefenseSlot(raw: unknown): CityHallDefenseSlot {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    if (Array.isArray(raw)) {
      const cards = normalizeGameCardsList(raw)
      return {
        cardIds: cards.map((c) => c.id).filter(Boolean),
        cards,
        power: cards.reduce((sum, c) => sum + (c.basePower ?? 0), 0),
      }
    }
    return emptyDefense()
  }
  const item = raw as RawRecord
  const cardsRaw = item.cards ?? item.defenseCards ?? item.defense_cards
  const cards = Array.isArray(cardsRaw) ? normalizeGameCardsList(cardsRaw) : []
  const idsRaw = item.cardIds ?? item.card_ids ?? item.ids
  const cardIds = Array.isArray(idsRaw)
    ? idsRaw.map((id) => String(id)).filter(Boolean)
    : cards.map((c) => c.id).filter(Boolean)
  const powerFromCards = cards.reduce((sum, c) => sum + (c.basePower ?? 0), 0)
  return {
    cardIds,
    cards,
    power: pickNumber(item.power, powerFromCards) ?? powerFromCards,
  }
}

function normalizeRaidPreview(raw: unknown): CityHallRaidPreview | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const tier = normalizeDefenseTier(
    item.defenseTier ?? item.defense_tier ?? item.tier
  )
  return {
    attackers:
      pickNumber(item.attackers ?? item.attackerCount ?? item.attacker_count, 0) ??
      0,
    attackCards:
      pickNumber(item.attackCards ?? item.attack_cards ?? item.attackCardCount, 0) ??
      0,
    attackPower: pickNumber(item.attackPower ?? item.attack_power, 0) ?? 0,
    defenseCards:
      pickNumber(
        item.defenseCards ?? item.defense_cards ?? item.defenseCardCount,
        0
      ) ?? 0,
    defenseTier: tier,
    defenseTierLabel: pickString(
      item.defenseTierLabel ?? item.defense_tier_label,
      tier === "STRONG"
        ? "Сильная"
        : tier === "MEDIUM"
          ? "Средняя"
          : tier === "WEAK"
            ? "Слабая"
            : "—"
    ),
    hullPower: pickNumber(item.hullPower ?? item.hull_power, 0) ?? 0,
  }
}

function normalizeRaidRewards(raw: unknown): CityHallRaidRewards {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...DEFAULT_CITY_HALL_RAID_REWARDS }
  }
  const item = raw as RawRecord
  return {
    winCoins:
      pickNumber(item.winCoins ?? item.win_coins, DEFAULT_CITY_HALL_RAID_REWARDS.winCoins) ??
      DEFAULT_CITY_HALL_RAID_REWARDS.winCoins,
    loseCoins:
      pickNumber(item.loseCoins ?? item.lose_coins, DEFAULT_CITY_HALL_RAID_REWARDS.loseCoins) ??
      DEFAULT_CITY_HALL_RAID_REWARDS.loseCoins,
    winPts:
      pickNumber(item.winPts ?? item.win_pts, DEFAULT_CITY_HALL_RAID_REWARDS.winPts) ??
      DEFAULT_CITY_HALL_RAID_REWARDS.winPts,
    losePts:
      pickNumber(item.losePts ?? item.lose_pts, DEFAULT_CITY_HALL_RAID_REWARDS.losePts) ??
      DEFAULT_CITY_HALL_RAID_REWARDS.losePts,
    winShardCommon:
      pickNumber(
        item.winShardCommon ?? item.win_shard_common ?? item.winShard,
        DEFAULT_CITY_HALL_RAID_REWARDS.winShardCommon
      ) ?? DEFAULT_CITY_HALL_RAID_REWARDS.winShardCommon,
    mvpPts:
      pickNumber(item.mvpPts ?? item.mvp_pts, DEFAULT_CITY_HALL_RAID_REWARDS.mvpPts) ??
      DEFAULT_CITY_HALL_RAID_REWARDS.mvpPts,
  }
}

function normalizeMyRaid(raw: unknown): CityHallMyRaid | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.raidId ?? item.raid_id
  const phase = normalizeRaidPhase(item.phase ?? item.status)
  if (id == null || !phase) return null

  const attackCardsRaw =
    item.myAttackCards ?? item.my_attack_cards ?? item.attackCards
  const myAttackCards = Array.isArray(attackCardsRaw)
    ? normalizeGameCardsList(attackCardsRaw)
    : []
  const idsRaw =
    item.myAttackCardIds ?? item.my_attack_card_ids ?? item.cardIds ?? item.card_ids
  const myAttackCardIds = Array.isArray(idsRaw)
    ? idsRaw.map((v) => String(v)).filter(Boolean)
    : myAttackCards.map((c) => c.id).filter(Boolean)

  return {
    id: String(id),
    phase,
    eventDate: pickString(item.eventDate ?? item.event_date) || null,
    voteEndsAt: pickString(item.voteEndsAt ?? item.vote_ends_at) || null,
    commitEndsAt: pickString(item.commitEndsAt ?? item.commit_ends_at) || null,
    targetCityId: pickNumber(item.targetCityId ?? item.target_city_id),
    targetCityTitle:
      pickString(item.targetCityTitle ?? item.target_city_title) || null,
    myVotedCityId: pickNumber(item.myVotedCityId ?? item.my_voted_city_id),
    attackerCount:
      pickNumber(item.attackerCount ?? item.attacker_count, 0) ?? 0,
    attackerCap:
      pickNumber(
        item.attackerCap ?? item.attacker_cap,
        CITY_HALL_RAID_ATTACKER_CAP_DEFAULT
      ) ?? CITY_HALL_RAID_ATTACKER_CAP_DEFAULT,
    joined: pickBool(item.joined ?? item.hasJoined ?? item.has_joined),
    myAttackCardIds,
    myAttackCards,
    remoteFeeCoins:
      pickNumber(
        item.remoteFeeCoins ?? item.remote_fee_coins,
        CITY_HALL_RAID_REMOTE_FEE_DEFAULT
      ) ?? CITY_HALL_RAID_REMOTE_FEE_DEFAULT,
    outcome: normalizeRaidOutcome(item.outcome),
    attackScore: pickNumber(item.attackScore ?? item.attack_score),
    defenseScore: pickNumber(item.defenseScore ?? item.defense_score),
    mvpNickname: pickString(item.mvpNickname ?? item.mvp_nickname) || null,
    mvpDelta: pickNumber(item.mvpDelta ?? item.mvp_delta),
    preview: normalizeRaidPreview(item.preview),
  }
}

function normalizeIncomingRaid(raw: unknown): CityHallIncomingRaid | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.raidId ?? item.raid_id
  const phase = normalizeRaidPhase(item.phase ?? item.status)
  if (id == null || !phase) return null
  return {
    id: String(id),
    phase,
    attackerCityId: pickNumber(item.attackerCityId ?? item.attacker_city_id),
    attackerCityTitle:
      pickString(item.attackerCityTitle ?? item.attacker_city_title) || null,
    attackerCount:
      pickNumber(item.attackerCount ?? item.attacker_count, 0) ?? 0,
    attackerCap:
      pickNumber(
        item.attackerCap ?? item.attacker_cap,
        CITY_HALL_RAID_ATTACKER_CAP_DEFAULT
      ) ?? CITY_HALL_RAID_ATTACKER_CAP_DEFAULT,
    preview: normalizeRaidPreview(item.preview),
    outcome: normalizeRaidOutcome(item.outcome),
    attackScore: pickNumber(item.attackScore ?? item.attack_score),
    defenseScore: pickNumber(item.defenseScore ?? item.defense_score),
    mvpNickname: pickString(item.mvpNickname ?? item.mvp_nickname) || null,
    mvpDelta: pickNumber(item.mvpDelta ?? item.mvp_delta),
  }
}

export function normalizeCityHallRaidStatus(raw: unknown): CityHallRaidStatus | null {
  const obj = unwrapPayload(raw)
  if (!obj || typeof obj !== "object") return null

  const pointRaw = obj.cityHallPoint ?? obj.city_hall_point ?? obj.point
  const defenseRaw = obj.myDefense ?? obj.my_defense ?? obj.defense

  return {
    isEventDay: pickBool(obj.isEventDay ?? obj.is_event_day),
    eventDate: pickString(obj.eventDate ?? obj.event_date) || null,
    cityHallPoint: normalizeCityHallPoint(pointRaw),
    defenseStreak:
      pickNumber(obj.defenseStreak ?? obj.defense_streak, 0) ?? 0,
    hullPower: pickNumber(obj.hullPower ?? obj.hull_power, 0) ?? 0,
    myDefense: normalizeDefenseSlot(defenseRaw),
    myRaid: normalizeMyRaid(obj.myRaid ?? obj.my_raid ?? obj.raid),
    incomingRaid: normalizeIncomingRaid(
      obj.incomingRaid ?? obj.incoming_raid ?? obj.incoming
    ),
    preview: normalizeRaidPreview(obj.preview),
    rewards: normalizeRaidRewards(obj.rewards),
    nextRaidLabel:
      pickString(obj.nextRaidLabel ?? obj.next_raid_label) || null,
  }
}

export function normalizeCityHallRaidTarget(raw: unknown): CityHallRaidTarget | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const cityId = pickNumber(item.cityId ?? item.city_id ?? item.id)
  if (cityId == null) return null
  const tier = normalizeDefenseTier(
    item.defenseTier ?? item.defense_tier ?? item.tier
  )
  return {
    cityId,
    title: pickString(item.title ?? item.name ?? item.cityTitle, `Город ${cityId}`),
    onCooldown: pickBool(item.onCooldown ?? item.on_cooldown),
    defenseStreak:
      pickNumber(item.defenseStreak ?? item.defense_streak, 0) ?? 0,
    hullPower: pickNumber(item.hullPower ?? item.hull_power, 0) ?? 0,
    defenseCards:
      pickNumber(
        item.defenseCards ?? item.defense_cards ?? item.defenseCardCount,
        0
      ) ?? 0,
    defenseTier: tier,
    defenseTierLabel: pickString(
      item.defenseTierLabel ?? item.defense_tier_label,
      tier === "STRONG"
        ? "Сильная"
        : tier === "MEDIUM"
          ? "Средняя"
          : tier === "WEAK"
            ? "Слабая"
            : "—"
    ),
  }
}

export function mapCityHallRaidTargets(data: unknown): CityHallRaidTarget[] {
  const obj = unwrapPayload(data)
  const listRaw =
    (Array.isArray(data) ? data : null) ??
    obj.targets ??
    obj.items ??
    obj.list ??
    obj.cities
  if (!Array.isArray(listRaw)) return []
  return listRaw
    .map(normalizeCityHallRaidTarget)
    .filter((t): t is CityHallRaidTarget => t != null)
}

function normalizeCombatCard(raw: unknown): CityHallRaidCombatCard | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const card = normalizeGameCard(raw)
  return {
    id: card?.id ?? (item.id != null ? String(item.id) : undefined),
    element: card?.element ?? (pickString(item.element) || undefined),
    colorRank:
      card?.colorRank ??
      (pickString(item.colorRank ?? item.color_rank) || undefined),
    level: card?.level ?? (pickNumber(item.level) ?? undefined),
    basePower:
      card?.basePower ??
      (pickNumber(item.basePower ?? item.base_power) ?? undefined),
    style: card?.style ?? (pickString(item.style) || undefined),
    title: pickString(item.title ?? item.name) || undefined,
  }
}

function normalizeCombatRound(
  raw: unknown,
  index: number
): CityHallRaidCombatRound | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  return {
    index: pickNumber(item.index, index) ?? index,
    text: pickString(item.text ?? item.message ?? item.line, ""),
    winnerSide: pickString(item.winnerSide ?? item.winner_side) || null,
    delta: pickNumber(item.delta),
    attackScoreAfter:
      pickNumber(
        item.attackScoreAfter ?? item.attack_score_after ?? item.attackScore,
        0
      ) ?? 0,
    defenseScoreAfter:
      pickNumber(
        item.defenseScoreAfter ?? item.defense_score_after ?? item.defenseScore,
        0
      ) ?? 0,
    attackerNickname:
      pickString(item.attackerNickname ?? item.attacker_nickname) || null,
    defenderNickname:
      pickString(item.defenderNickname ?? item.defender_nickname) || null,
    attackCard: normalizeCombatCard(
      item.attackCard ?? item.attack_card ?? item.attackerCard
    ),
    defenseCard: normalizeCombatCard(
      item.defenseCard ?? item.defense_card ?? item.defenderCard
    ),
  }
}

function normalizeCombatLog(raw: unknown): CityHallRaidCombatLog | null {
  if (!raw) return null
  if (Array.isArray(raw)) {
    const rounds = raw
      .map((item, index) => normalizeCombatRound(item, index))
      .filter((r): r is CityHallRaidCombatRound => r != null)
    return { rounds }
  }
  if (typeof raw !== "object") return null
  const item = raw as RawRecord
  const roundsRaw = item.rounds ?? item.items ?? item.log ?? item.entries
  const rounds = Array.isArray(roundsRaw)
    ? roundsRaw
        .map((entry, index) => normalizeCombatRound(entry, index))
        .filter((r): r is CityHallRaidCombatRound => r != null)
    : []
  return {
    rounds,
    outcome: normalizeRaidOutcome(item.outcome),
    mvpNickname: pickString(item.mvpNickname ?? item.mvp_nickname) || null,
    mvpDelta: pickNumber(item.mvpDelta ?? item.mvp_delta),
    attackScore: pickNumber(item.attackScore ?? item.attack_score),
    defenseScore: pickNumber(item.defenseScore ?? item.defense_score),
  }
}

export function normalizeCityHallRaidDetail(raw: unknown): CityHallRaidDetail | null {
  const obj = unwrapPayload(raw)
  const id = obj.id ?? obj.raidId ?? obj.raid_id
  if (id == null) return null
  const phase =
    normalizeRaidPhase(obj.phase ?? obj.status) ??
    ("RESOLVED" as CityHallRaidPhase)
  const combatLog = normalizeCombatLog(obj.combatLog ?? obj.combat_log)
  const nestedStatus = normalizeCityHallRaidStatus(
    obj.status ?? obj.raidStatus ?? obj.raid_status
  )
  return {
    id: String(id),
    phase,
    outcome: normalizeRaidOutcome(obj.outcome),
    attackScore: pickNumber(obj.attackScore ?? obj.attack_score),
    defenseScore: pickNumber(obj.defenseScore ?? obj.defense_score),
    mvpNickname: pickString(obj.mvpNickname ?? obj.mvp_nickname) || null,
    mvpDelta: pickNumber(obj.mvpDelta ?? obj.mvp_delta),
    combatLog,
    preview: normalizeRaidPreview(obj.preview),
    attackerCityTitle:
      pickString(obj.attackerCityTitle ?? obj.attacker_city_title) || null,
    defenderCityTitle:
      pickString(obj.defenderCityTitle ?? obj.defender_city_title) || null,
    status: nestedStatus,
  }
}

export function normalizeCityHallRaidGeoBody(body: {
  cardIds: string[]
  latitude?: number | null
  longitude?: number | null
}): CityHallRaidGeoBody {
  const cardIds = (body.cardIds ?? [])
    .map((id) => String(id).trim())
    .filter(Boolean)
    .slice(0, 3)
  const lat =
    body.latitude != null && Number.isFinite(body.latitude)
      ? Number(Number(body.latitude).toFixed(6))
      : null
  const lng =
    body.longitude != null && Number.isFinite(body.longitude)
      ? Number(Number(body.longitude).toFixed(6))
      : null
  return {
    cardIds,
    latitude: lat,
    longitude: lng,
  }
}

export function formatCityHallRaidPhase(
  phase: CityHallRaidPhase | null | undefined
): string {
  if (phase === "VOTE") return "Голосование"
  if (phase === "COMMIT") return "Сбор атаки"
  if (phase === "RESOLVED") return "Бой"
  if (phase === "CANCELLED") return "Отменён"
  return "—"
}

export function formatCityHallRaidOutcome(
  outcome: CityHallRaidOutcome | null | undefined
): string {
  const key = pickString(outcome).toUpperCase()
  if (key === "WIN") return "Победа"
  if (key === "LOSE") return "Поражение"
  if (key === "DRAW") return "Ничья"
  return key || "—"
}

export function defaultNextRaidLabel(isEventDay: boolean): string {
  if (isEventDay) return "Рейд сегодня"
  return "Следующий рейд: Чт/Вс 18:00 (Минск)"
}
